/**
 * Modelos do projeto — os veículos que aquele conteúdo atende.
 *
 * O modelo é o que amarra o projeto ao mundo real: veículo, motor, faixa de ano
 * e produto. É por ele que a RN-01 decide o que aparece (pinout restrito a
 * outro modelo some da vista) e é ele que se publica.
 *
 * **Os catálogos aqui são globais, não do projeto.** Veículo e motor são listas
 * do sistema inteiro — 1.202 e 1.395 itens — e vêm de uma chamada só por
 * sessão, como manda a regra 1. Por isso entram em campo de busca com filtro em
 * memória, e não em `<select>`: mil e duzentas opções num select é uma lista
 * que ninguém percorre.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import { catalogoPorProjeto, chaves } from "./query"
import type { ItemDeCatalogo } from "./catalogos"

export type ModeloResponse = {
  id: number
  descricao: string
  veiculoId: number
  motorId: number
  anoIni: string | null
  anoFim: string | null
  publicado: boolean
  publicadoData: string | null
  projetoId: number
  produtoId: number
  veiculo: string
  motor: string
  produtoNome: string
}

export type ModeloRequest = {
  projetoId: number
  descricao: string
  veiculoId: number
  motorId: number
  anoIni: string | null
  anoFim: string | null
  produtoId: number
}

export function usarModelosDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: ["modelos", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<ModeloResponse[]>("api/modelos", { projetoId }),
  })
}

export function usarSalvarModelo(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number | null; dados: ModeloRequest }) =>
      id
        ? (await api.put<ModeloResponse>(`api/modelos/${id}`, dados, noProjeto(projetoId))).data
        : (await api.post<ModeloResponse>("api/modelos", dados)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["modelos", projetoId] })
      qc.invalidateQueries({ queryKey: ["catalogo", "modelos", projetoId] })
      qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
    },
  })
}

export function usarExcluirModelo(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => { await api.delete(`api/modelos/${id}`, noProjeto(projetoId)) },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["modelos", projetoId] })
      qc.invalidateQueries({ queryKey: ["catalogo", "modelos", projetoId] })
    },
  })
}

/**
 * Publicar e despublicar. Publicar marca o projeto para reprocessamento (RN-05)
 * — é o que faz o gráfico ser refeito com o modelo novo no ar.
 */
export function usarPublicarModelo(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, publicar }: { id: number; publicar: boolean }) => {
      await api.post(
        `api/modelos/${id}/${publicar ? "publicar" : "despublicar"}`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["modelos", projetoId] })
      qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
    },
  })
}

/**
 * Os modelos em que o pinout de um componente aparece no site: os que a
 * restrição de algum pinout dele alcança. Pinout sem restrição não aparece em
 * modelo nenhum — é a regra do site, não desta tela.
 */
export function usarModelosDoComponente(componenteId: number) {
  return useQuery({
    queryKey: ["modelos-do-componente", componenteId] as const,
    enabled: componenteId > 0,
    queryFn: () => obter<ModeloResponse[]>(`api/modelos/do-componente/${componenteId}`, {}),
  })
}

/**
 * Publica de uma vez todos os modelos que o componente alcança. É o gesto do
 * dia a dia: escolhe-se o componente, não modelo a modelo. Os já publicados
 * ficam como estão.
 */
export function usarPublicarModelosDoComponente(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (componenteId: number) =>
      (await api.post<{ publicados: number }>(
        `api/modelos/do-componente/${componenteId}/publicar`, undefined, noProjeto(projetoId))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["modelos-do-componente"] })
      qc.invalidateQueries({ queryKey: ["modelos", projetoId] })
      qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
    },
  })
}

// ------------------------------------------------------- catálogos globais

type VeiculoResponse = { id: number; modelo: string; detalhes: string; fipe: string; marcaId: number }
type MotorResponse = { id: number; nome: string; detalhes: string; marcaId: number }
type MarcaResponse = { id: number; nome: string }
export type ProdutoResponse = { id: number; nome: string; codigo: number; tipo: number; exibicao: number }

/**
 * Veículo e motor têm um registro de id 0 com todos os campos em branco — lixo
 * do cadastro antigo, não escolha. **Produto não**: ali o id 0 é "Caminhões",
 * um produto de verdade, e filtrá-lo esconderia um produto que existe.
 */
const real = (x: { id: number }) => x.id > 0

/**
 * A marca entra no rótulo de veículo e de motor.
 *
 * São 1.202 veículos e 1.395 motores numa lista só, e "i30" sem "Hyundai" na
 * frente é ambíguo — escolher o veículo errado num modelo é erro que só aparece
 * depois, no conteúdo publicado. São 73 marcas, uma chamada por sessão.
 */
function usarMarcas() {
  const { data } = useQuery({
    queryKey: ["catalogo", "marcas"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<MarcaResponse[]>("api/marcas", {}),
  })
  return new Map((data ?? []).map((m) => [m.id, m.nome]))
}

export function usarVeiculos() {
  const marcas = usarMarcas()
  const { data, isPending } = useQuery({
    queryKey: ["catalogo", "veiculos"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<VeiculoResponse[]>("api/veiculos", {}),
  })
  const itens: ItemDeCatalogo[] = (data ?? []).filter(real).map((v) => ({
    id: v.id,
    rotulo: [marcas.get(v.marcaId), v.modelo, v.detalhes]
      .filter((x) => x && x.trim()).join(" "),
    codigo: v.fipe || undefined,
    // `usos` ordena catálogo por frequência nos pinouts. Veículo e motor não
    // têm essa contagem — são cadastro global, não uso no projeto.
    usos: 0,
  }))
  return { itens, carregando: isPending }
}

export function usarMotores() {
  const marcas = usarMarcas()
  const { data, isPending } = useQuery({
    queryKey: ["catalogo", "motores"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<MotorResponse[]>("api/motores", {}),
  })
  // `nome` já vem composto do cadastro ("1.6 16V L4 Gamma Flex") e é o mesmo
  // texto que a listagem de modelos mostra. Juntar cilindrada e combustível
  // repetia o que já está ali — dava "1.6 16V L4 Gamma Flex · 1.6 · Flex", e
  // "1.6 · 1.6" nos registros de cadastro mais pobre.
  const itens: ItemDeCatalogo[] = (data ?? []).filter(real).map((m) => ({
    id: m.id,
    rotulo: [marcas.get(m.marcaId), m.nome || m.detalhes]
      .filter((x) => x && x.trim()).join(" "),
    usos: 0,
  }))
  return { itens, carregando: isPending }
}

export function usarProdutos() {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo", "produtos"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<ProdutoResponse[]>("api/produtos", {}),
  })
  // Sem `real` aqui de propósito: ver a nota acima. O produto de id 0 existe.
  return { itens: data ?? [], carregando: isPending }
}
